import React, { useCallback, useState } from 'react';
import { isNull } from 'lodash/fp';

import { Icon } from '../../shared/icon/Icon';
import { Robot } from '../../shared/icons';
import styles from './DatasetInstance.module.scss';
import { CustomTooltip } from '../../shared/tooltip';
import { Text } from '../../shared/text/Text';
import { useAppDispatch, useAppSelector } from '../../../hooks/react-redux';
import { getInstanceEcsEndPage, getInstanceEcsStartPage, updateInstanceEcsPage, upsertEcsPageInfoStarted } from './store';
import { Button } from '../../shared/button/Button';

const { amber, primary } = styles;

interface ECSPageCaptureProps {
    disabled: boolean;
}

export const ECSPageCapture: React.FC<ECSPageCaptureProps> = ({ disabled }) => {
    const [pageCaptureOpen, setPageCaptureOpen] = useState<boolean>(false);
    const dispatch = useAppDispatch();

    const ecsStartPage = useAppSelector(getInstanceEcsStartPage);
    const ecsEndPage = useAppSelector(getInstanceEcsEndPage);

    const updatePageReference = useCallback((key: 'instanceEcsStartPage' | 'instanceEcsEndPage', value: string) => {
        if (!isNaN(parseInt(value)) || value === '') {
            dispatch(updateInstanceEcsPage(key, value === '' ? null : parseInt(value)));
        }
    }, [dispatch]);

    const updateECSStartPage = useCallback((value: string) => updatePageReference('instanceEcsStartPage', value), [updatePageReference]);
    const updateECSEndPage = useCallback((value: string) => updatePageReference('instanceEcsEndPage', value), [updatePageReference]);

    const updateECSPageInfo = useCallback(() => dispatch(upsertEcsPageInfoStarted()), [dispatch]);

    return (
        <div className={styles.ecsPageCaptureWrapper}>
            <div onClick={() => !disabled && setPageCaptureOpen(!pageCaptureOpen)} className={styles.ecsPageCaptureHeader} style={{ color: isNull(ecsStartPage) || isNull(ecsEndPage) ? amber : primary }}>
                <div className={styles.ecsPageCaptureLabel}>Attempt ECS Data Extraction with AI</div>
                <Icon icon={Robot} fontSize={24} />
            </div>
            {pageCaptureOpen &&
                <div className={styles.ecsPageCapture}>
                    <div className={styles.ecsPageInfo}>
                        <div className={styles.ecsPageWrapper}>
                            <div className={styles.ecsPageLabelWrapper}>
                                <CustomTooltip overlayText='Eligible Collateral Schedule Start Page'>
                                    <div className={styles.ecsPageLabel}>ECS Start Page:</div>
                                </CustomTooltip>
                            </div>
                            <div className={styles.ecsPageInput}>
                                <Text
                                    testId='ecs-start-page'
                                    value={ecsStartPage?.toString()}
                                    onChange={e => updateECSStartPage(e.target.value)}
                                    marginBottom='0px'
                                    width='40%'
                                    marginRight='10px'
                                    borderColour={isNull(ecsStartPage) ? amber : primary}
                                    disabled={disabled}
                                />
                            </div>
                        </div>
                        <div className={styles.ecsPageWrapper} style={{ marginLeft: '10px' }}>
                            <div className={styles.ecsPageLabelWrapper}>
                                <CustomTooltip overlayText='Eligible Collateral Schedule End Page'>
                                    <div className={styles.ecsPageLabel}>ECS End Page:</div>
                                </CustomTooltip>
                            </div>
                            <div className={styles.ecsPageInput}>
                                <Text
                                    testId='ecs-end-page'
                                    value={ecsEndPage?.toString()}
                                    onChange={e => updateECSEndPage(e.target.value)}
                                    marginBottom='0px'
                                    width='40%'
                                    marginRight='10px'
                                    borderColour={isNull(ecsEndPage) ? amber : primary}
                                    disabled={disabled}
                                />
                            </div>
                        </div>
                        <div className={styles.buttonWrapper}>
                            {!isNull(ecsStartPage) && !isNull(ecsEndPage) && <Button label='Confirm' onClick={updateECSPageInfo} margin='0' />}
                        </div>
                    </div>
                </div>
            }
        </div>
    );
};
